import { useAuthStore } from '@/stores/authStore';

// Map legacy IANA names that browsers may not recognize
const LEGACY_MAP = {
    'US/Samoa': 'Pacific/Pago_Pago',
    'US/East-Indiana': 'America/Indiana/Indianapolis',
    'Canada/Saskatchewan': 'America/Regina',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'Greenland': 'America/Godthab',
    'Europe/Kiev': 'Europe/Kyiv',
    'Asia/Chongqing': 'Asia/Shanghai',
    'Australia/Canberra': 'Australia/Sydney',
};

function resolveTimezone(tz) {
    if (!tz) return undefined;
    return LEGACY_MAP[tz] || tz;
}

function getUserTimezone() {
    try {
        const authStore = useAuthStore();
        return resolveTimezone(authStore.user?.timezone);
    } catch {
        return undefined;
    }
}

/**
 * Format a UTC timestamp string to the user's timezone.
 * @param {string|Date} ts - UTC timestamp (string or Date)
 * @param {object} [opts] - Intl.DateTimeFormat options override
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(ts, opts) {
    if (!ts) return '';
    let dateStr = ts instanceof Date ? ts : String(ts);
    if (typeof dateStr === 'string') {
        // Ensure UTC parsing: add 'Z' if no timezone indicator
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
            dateStr = dateStr.replace(' ', 'T') + 'Z';
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', dateStr.indexOf('T'))) {
            dateStr += 'Z';
        }
        dateStr = new Date(dateStr);
    }
    if (isNaN(dateStr.getTime())) return '';

    const tz = getUserTimezone();
    const defaultOpts = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true,
    };
    const formatOpts = { ...defaultOpts, ...opts };
    if (tz) formatOpts.timeZone = tz;

    try {
        return new Intl.DateTimeFormat('en-US', formatOpts).format(dateStr);
    } catch {
        // Fallback if timezone is invalid
        return dateStr.toLocaleString('en');
    }
}

/**
 * Format a UTC timestamp to short date only (e.g., "3/1/2026").
 */
export function formatDate(ts) {
    return formatDateTime(ts, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: undefined,
        minute: undefined,
        second: undefined,
    });
}

export function useTimezone() {
    return { formatDateTime, formatDate };
}
